import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { AdvancedJson } from "../components/phase3";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type EntitlementSnapshotView,
  type ServerDirectoryEntry,
  type Workspace,
  type WorkspaceDeletionPreview,
  type WorkspaceDeletionResult,
  type WorkspaceMember,
} from "../lib/api";

/**
 * Settings → Workspace (Part 3).
 *
 * A workspace is the thing a customer's Discord servers, snapshots, BALDER
 * history and subscription all hang off, and until now it was only ever visible
 * as a name in a dropdown. This page makes it inspectable: who owns it, who is
 * in it, which Discord servers it holds, what the plan allows it — and how to
 * delete it without being surprised by what that costs.
 *
 * Three rules shape the deletion half of the page:
 *
 *   - The confirmation text is written by the server, not by this file. Only the
 *     server knows how many snapshots exist or which Discord servers are
 *     attached, and a dialog that guesses those numbers is not a confirmation.
 *     `consequences` and `preserved` are rendered verbatim.
 *   - The browser never decides that a deletion was confirmed. The typed name is
 *     sent to the server, which refuses anything that does not match. The local
 *     check exists only so the button is not offered before it would work, and
 *     it is deliberately no stricter than the server's (trimmed, case-insensitive)
 *     — a customer who typed the right thing must never face a dead button.
 *   - Deleting a workspace does not remove ODIN from Discord. The server says so
 *     in `consequences`; this page repeats nothing and invents nothing.
 *
 * Every panel loads independently. A section that fails says it failed — it
 * never renders as "empty", because "we could not read your members" and "this
 * workspace has no members" are opposite facts.
 */

/**
 * Roles allowed to rename a workspace and to ask what deleting it would cost,
 * mirroring `canManageTenant` server-side.
 *
 * Deletion itself is owner-only, and that is decided by the server: an admin who
 * opens the preview gets a `not_owner` blocker back and sees it spelled out,
 * which is more useful than hiding the section and leaving them to guess.
 */
const MANAGER_ROLES = new Set(["OWNER", "ADMIN"]);

export function canManageWorkspace(role: string | null): boolean {
  return role !== null && MANAGER_ROLES.has(role);
}

/**
 * Turn a storage model name into something a customer can read.
 *
 * The purge counts come back keyed by ODIN's internal table names. Rather than
 * keep a hand-written dictionary that silently falls out of date the next time a
 * table is added, this de-camel-cases whatever it is given: an unfamiliar name
 * degrades to slightly clumsy English rather than to a leak of schema jargon.
 */
export function purgeLabel(model: string): string {
  const spaced = model.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  const sentence = spaced.charAt(0).toUpperCase() + spaced.slice(1);
  return sentence.endsWith("s") ? sentence : `${sentence}s`;
}

/** Total records removed, used so the summary leads with one honest number. */
export function purgeTotal(purged: Record<string, number>): number {
  return Object.values(purged).reduce((sum, n) => sum + n, 0);
}

/** A customer-facing sentence for a failed delete, preferring the server's own words. */
export function deleteErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "rate_limited") {
      return "That's several delete attempts in a short time. Nothing was deleted — wait a minute and try again.";
    }
    if (error.code === "forbidden") {
      return "Only an owner of this workspace can delete it. Nothing was deleted.";
    }
    // conflict / invalid_request / not_found all carry a server-written reason
    // that names the specific obstacle, which is more useful than anything
    // this file could say.
    return error.message;
  }
  return "We couldn't delete this workspace, and nothing was changed. Please try again.";
}

// ── Identity ─────────────────────────────────────────────────────────────────

export function WorkspaceIdentityCard({
  workspace,
  role,
  onRename,
  busy,
  error,
}: {
  workspace: Workspace;
  role: string | null;
  onRename: (name: string) => void;
  busy: boolean;
  error: string | null;
}): JSX.Element {
  const [draft, setDraft] = useState(workspace.name);
  useEffect(() => setDraft(workspace.name), [workspace.name]);
  const trimmed = draft.trim();
  const unchanged = trimmed === workspace.name;
  return (
    <Card title="Workspace">
      <div className="kv-grid">
        <div className="kv">
          <span className="kv__k">Name</span>
          <span className="kv__v">{workspace.name}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Identifier</span>
          <span className="kv__v mono">{workspace.slug}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Your role</span>
          <span className="kv__v">{role ?? "—"}</span>
        </div>
      </div>
      {canManageWorkspace(role) ? (
        <form
          className="row"
          style={{ gap: "0.5rem", flexWrap: "wrap", marginTop: "0.75rem" }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!unchanged && trimmed.length >= 2) onRename(trimmed);
          }}
        >
          <label className="kv__k" htmlFor="workspace-name">
            Rename workspace
          </label>
          <input
            id="workspace-name"
            className="field__input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
          />
          <Button type="submit" variant="secondary" disabled={busy || unchanged || trimmed.length < 2}>
            {busy ? "Saving…" : "Save name"}
          </Button>
        </form>
      ) : (
        <p className="ax-metric__foot">
          Renaming a workspace is limited to its owners and admins.
        </p>
      )}
      {error ? (
        <p className="ax-confirm__error" role="alert">
          {error}
        </p>
      ) : null}
    </Card>
  );
}

// ── People ───────────────────────────────────────────────────────────────────

export function MembersCard({ members }: { members: WorkspaceMember[] | null }): JSX.Element {
  if (members === null) {
    return (
      <Card title="People">
        <p className="ax-confirm__error" role="alert">
          We couldn&apos;t read the member list for this workspace. This is a display problem only —
          nobody&apos;s access has changed.
        </p>
      </Card>
    );
  }
  const owners = members.filter((m) => m.role === "OWNER");
  return (
    <Card title="People">
      <p className="muted">
        {owners.length === 1
          ? `${owners[0]?.username ?? "Someone"} owns this workspace.`
          : `${owners.length} people own this workspace.`}{" "}
        Owners are the only ones who can delete it or transfer it.
      </p>
      <ul className="chip-list" style={{ flexDirection: "column", alignItems: "flex-start" }}>
        {members.map((member) => (
          <li key={member.userId} className="row" style={{ gap: "0.5rem" }}>
            <span>{member.username}</span>
            <span className="count-chip">{member.role}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ── Connected Discord servers ────────────────────────────────────────────────

export function ServersCard({ servers }: { servers: ServerDirectoryEntry[] | null }): JSX.Element {
  if (servers === null) {
    return (
      <Card title="Connected Discord servers">
        <p className="ax-confirm__error" role="alert">
          We couldn&apos;t read the server list right now. Nothing has been disconnected.
        </p>
      </Card>
    );
  }
  const connected = servers.filter((s) => s.guildId !== null);
  return (
    <Card title="Connected Discord servers">
      {connected.length === 0 ? (
        <p className="muted">
          No Discord server is connected to this workspace yet.{" "}
          <Link to="/app/servers" className="ax-card__link">
            Connect one
          </Link>
        </p>
      ) : (
        <>
          <ul className="chip-list" style={{ flexDirection: "column", alignItems: "flex-start" }}>
            {connected.map((server) => (
              <li key={server.discordGuildId} className="row" style={{ gap: "0.5rem" }}>
                <span>{server.name}</span>
                <span className="count-chip">
                  {server.memberCount === null ? "— members" : `${server.memberCount} members`}
                </span>
              </li>
            ))}
          </ul>
          <p className="ax-metric__foot">
            Disconnecting a single server, or removing ODIN from it, is done per server on the{" "}
            <Link to="/app/servers" className="ax-card__link">
              servers page
            </Link>
            .
          </p>
        </>
      )}
    </Card>
  );
}

// ── Plan limits ──────────────────────────────────────────────────────────────

export function PlanLimitsCard({ plan }: { plan: EntitlementSnapshotView | null }): JSX.Element {
  if (plan === null) {
    return (
      <Card title="What this workspace is allowed">
        <p className="ax-confirm__error" role="alert">
          We couldn&apos;t read the plan limits for this workspace. Your subscription is unaffected —{" "}
          <Link to="/app/billing" className="ax-card__link">
            billing
          </Link>{" "}
          has the authoritative view.
        </p>
      </Card>
    );
  }
  const measured = plan.entitlements.filter((e) => e.limit !== null);
  return (
    <Card title="What this workspace is allowed">
      <p className="muted">
        Plan <span className="mono">{plan.planCode}</span> · state{" "}
        <span className="mono">{plan.subscriptionState}</span>. Limits apply to this workspace only.
      </p>
      {measured.length === 0 ? (
        <p className="muted">This plan has no counted limits on this workspace.</p>
      ) : (
        <ul className="chip-list" style={{ flexDirection: "column", alignItems: "flex-start" }}>
          {measured.map((e) => (
            <li key={e.key} className="muted">
              <span className="mono">{e.key}</span>: {e.usage ?? 0} of {e.limit} used. {e.explanation}
            </li>
          ))}
        </ul>
      )}
      <div className="row" style={{ marginTop: "0.5rem" }}>
        <Link to="/app/billing" className="ax-card__link">
          Manage plan
        </Link>
      </div>
    </Card>
  );
}

// ── Danger zone ──────────────────────────────────────────────────────────────

export type PreviewState =
  | { kind: "closed" }
  | { kind: "loading" }
  | { kind: "error"; detail: string }
  | { kind: "ready"; data: WorkspaceDeletionPreview };

/**
 * Whether the typed confirmation matches, using exactly the server's own rule.
 *
 * Being stricter here would be worse than useless: a customer who typed their
 * workspace name correctly would face a button that never enables, with nothing
 * on screen explaining why.
 */
export function confirmationMatches(typed: string, phrase: string): boolean {
  return typed.trim().toLowerCase() === phrase.trim().toLowerCase() && phrase.trim().length > 0;
}

export function DeletePanel({
  preview,
  typed,
  onTypedChange,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  preview: WorkspaceDeletionPreview;
  typed: string;
  onTypedChange: (next: string) => void;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  const matches = confirmationMatches(typed, preview.confirmationPhrase);
  const blocked = !preview.canDelete;
  return (
    <section className="ax-confirm" aria-label={`Delete the workspace ${preview.name}`}>
      <h3 className="ax-confirm__title">Delete the workspace “{preview.name}”?</h3>

      <ul className="ax-confirm__list">
        {preview.consequences.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      {preview.preserved.length > 0 ? (
        <>
          <h4 className="ax-confirm__subtitle">What ODIN keeps</h4>
          <ul className="ax-confirm__list">
            {preview.preserved.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      ) : null}

      {preview.isFinalWorkspace && preview.replacementWorkspaceName ? (
        <p className="ax-metric__foot">
          This is your last workspace. Deleting it creates an empty workspace called “
          {preview.replacementWorkspaceName}” so your account, your subscription and your sign-in
          still have somewhere to land.
        </p>
      ) : null}

      {blocked ? (
        <div role="alert">
          <h4 className="ax-confirm__subtitle">This workspace can&apos;t be deleted yet</h4>
          <ul className="ax-confirm__list">
            {preview.blockers.map((blocker) => (
              <li key={blocker.code}>
                <strong>{blocker.message}</strong> {blocker.resolution}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <label className="ax-confirm__check" style={{ display: "grid", gap: "0.35rem" }}>
          <span>
            Type <strong>{preview.confirmationPhrase}</strong> to confirm.
          </span>
          <input
            className="field__input"
            value={typed}
            onChange={(e) => onTypedChange(e.target.value)}
            disabled={busy}
            autoComplete="off"
            aria-label={`Type ${preview.confirmationPhrase} to confirm deletion`}
          />
        </label>
      )}

      {error ? (
        <p className="ax-confirm__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="ax-rail-actions" style={{ marginTop: "0.75rem" }}>
        <button
          type="button"
          className="ax-btn ax-btn--danger"
          onClick={onConfirm}
          disabled={busy || blocked || !matches}
        >
          {busy ? "Deleting…" : "Permanently delete this workspace"}
        </button>
        <button type="button" className="ax-btn ax-btn--secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </section>
  );
}

export function DangerZone({
  role,
  preview,
  typed,
  onTypedChange,
  busy,
  error,
  onOpen,
  onConfirm,
  onCancel,
}: {
  role: string | null;
  preview: PreviewState;
  typed: string;
  onTypedChange: (next: string) => void;
  busy: boolean;
  error: string | null;
  onOpen: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  if (!canManageWorkspace(role)) {
    return (
      <Card title="Delete this workspace">
        <p className="muted">
          Deleting a workspace is limited to its owners. Ask an owner if this workspace should go
          away.
        </p>
      </Card>
    );
  }
  return (
    <Card title="Delete this workspace">
      <p className="muted">
        Deleting a workspace permanently removes what ODIN stores for it. Before anything happens
        we&apos;ll show you exactly what that includes.
      </p>
      {preview.kind === "closed" ? (
        <div className="row" style={{ marginTop: "0.5rem" }}>
          <Button variant="secondary" onClick={onOpen}>
            Show me what deleting this would do
          </Button>
        </div>
      ) : null}
      {preview.kind === "loading" ? <LoadingState label="Checking what this would affect…" /> : null}
      {preview.kind === "error" ? (
        <>
          <p className="ax-confirm__error" role="alert">
            {preview.detail}
          </p>
          <div className="row" style={{ marginTop: "0.5rem" }}>
            <Button variant="secondary" onClick={onOpen}>
              Try again
            </Button>
          </div>
        </>
      ) : null}
      {preview.kind === "ready" ? (
        <DeletePanel
          preview={preview.data}
          typed={typed}
          onTypedChange={onTypedChange}
          busy={busy}
          error={error}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      ) : null}
    </Card>
  );
}

// ── Deleted confirmation ─────────────────────────────────────────────────────

export function DeletedSummary({ result }: { result: WorkspaceDeletionResult }): JSX.Element {
  const total = purgeTotal(result.purged);
  const rows = Object.entries(result.purged).filter(([, n]) => n > 0);
  return (
    <>
      <PageHeader title="Workspace deleted" />
      <Card title={`“${result.name}” is gone`}>
        <StatusIndicator tone="ok" label="Deleted" />
        <p style={{ marginTop: "0.5rem" }}>
          {total === 0
            ? "There was nothing stored for this workspace, so nothing had to be removed."
            : `${total} stored records were permanently removed.`}
        </p>
        {rows.length > 0 ? (
          <ul className="chip-list" style={{ flexDirection: "column", alignItems: "flex-start" }}>
            {rows.map(([model, count]) => (
              <li key={model} className="muted">
                {purgeLabel(model)}: {count}
              </li>
            ))}
          </ul>
        ) : null}
        <p className="ax-metric__foot">
          ODIN is still in whatever Discord servers it was in. Removing the bot from a server is a
          separate action you take per server, from Discord or from the servers page.
        </p>
        {result.replacement ? (
          <p style={{ marginTop: "0.5rem" }}>
            That was your last workspace, so we created an empty one called “{result.replacement.name}
            ” and switched you to it. Your account and subscription history are untouched.
          </p>
        ) : (
          <p style={{ marginTop: "0.5rem" }}>
            Pick another workspace from the switcher to carry on.
          </p>
        )}
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <Link to="/app" className="ax-card__link">
            Back to the dashboard
          </Link>
        </div>
        <AdvancedJson title="Advanced — what was removed" value={result.purged} />
      </Card>
    </>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

interface PageData {
  workspace: Workspace;
  role: string | null;
  members: WorkspaceMember[] | null;
  servers: ServerDirectoryEntry[] | null;
  plan: EntitlementSnapshotView | null;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: PageData }
  | { kind: "unavailable"; detail?: string };

/** Resolve to `null` instead of rejecting, so one dead panel can't blank the page. */
async function settled<T>(promise: Promise<T>): Promise<T | null> {
  try {
    return await promise;
  } catch {
    return null;
  }
}

export function WorkspaceSettings(): JSX.Element {
  const ctx = useAppContext();
  const tenantId = ctx.tenantId;
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  /**
   * Terminal, and deliberately outside `LoadState`.
   *
   * Deleting the active workspace changes the selected tenant, which would
   * otherwise re-trigger the loader — and the loader would then look up a
   * workspace that no longer exists and report the account as broken, wiping the
   * receipt for the thing the customer just did. Once this is set, the page is
   * finished loading anything.
   */
  const [deleted, setDeleted] = useState<WorkspaceDeletionResult | null>(null);
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState>({ kind: "closed" });
  const [typed, setTyped] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!apiConfigured || !tenantId || deleted) return;
    setState({ kind: "loading" });
    try {
      // The workspace list is the one call that must succeed: it carries both the
      // name and the caller's role, and every other panel is decoration without it.
      const list = await api.listWorkspaces();
      const workspace = list.workspaces.find((w) => w.id === tenantId);
      if (!workspace) {
        setState({
          kind: "unavailable",
          detail: "This workspace is no longer available to your account.",
        });
        return;
      }
      const [members, directory, plan] = await Promise.all([
        settled(api.workspaceMembers(tenantId)),
        settled(api.listServers(tenantId)),
        settled(api.billing.entitlements(tenantId)),
      ]);
      setState({
        kind: "ready",
        data: {
          workspace,
          role: workspace.role ?? null,
          members: members?.members ?? null,
          servers: directory?.servers ?? null,
          plan: plan?.entitlements ?? null,
        },
      });
    } catch (error) {
      setState({
        kind: "unavailable",
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  }, [deleted, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRename = useCallback(
    async (name: string) => {
      if (!tenantId) return;
      setRenameBusy(true);
      setRenameError(null);
      setNotice(null);
      try {
        const updated = await api.renameWorkspace(tenantId, name);
        ctx.setWorkspace(updated.id, updated.name);
        setNotice(`This workspace is now called “${updated.name}”.`);
        setState((s) =>
          s.kind === "ready"
            ? { kind: "ready", data: { ...s.data, workspace: { ...s.data.workspace, ...updated } } }
            : s,
        );
        // The name is the confirmation phrase, so a rename invalidates any
        // preview already on screen.
        setPreview({ kind: "closed" });
        setTyped("");
      } catch (error) {
        setRenameError(
          error instanceof ApiError
            ? error.message
            : "We couldn't rename this workspace. Nothing was changed.",
        );
      } finally {
        setRenameBusy(false);
      }
    },
    [ctx, tenantId],
  );

  const onOpenPreview = useCallback(async () => {
    if (!tenantId) return;
    setPreview({ kind: "loading" });
    setDeleteError(null);
    setTyped("");
    try {
      setPreview({ kind: "ready", data: await api.workspaceDeletePreview(tenantId) });
    } catch (error) {
      setPreview({
        kind: "error",
        detail:
          error instanceof ApiError
            ? error.message
            : "We couldn't work out what deleting this workspace would affect, so we're not offering the button.",
      });
    }
  }, [tenantId]);

  const onConfirmDelete = useCallback(async () => {
    if (!tenantId) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const result = await api.deleteWorkspace(tenantId, typed);
      setDeleted(result);
      // The deleted id is persisted in local storage; leaving it there would put
      // every other page into a workspace that no longer exists.
      if (result.replacement) {
        ctx.setWorkspace(result.replacement.id, result.replacement.name);
      } else {
        ctx.clearWorkspace();
      }
    } catch (error) {
      setDeleteError(deleteErrorMessage(error));
    } finally {
      setDeleteBusy(false);
    }
  }, [ctx, tenantId, typed]);

  const onCancelDelete = useCallback(() => {
    setPreview({ kind: "closed" });
    setTyped("");
    setDeleteError(null);
  }, []);

  if (!apiConfigured) {
    return (
      <>
        <PageHeader title="Workspace" />
        <NotConfiguredState title="Control API URL not configured">
          Set <code>PUBLIC_API_URL</code> when building the dashboard to manage workspaces.
        </NotConfiguredState>
      </>
    );
  }

  if (deleted) {
    return <DeletedSummary result={deleted} />;
  }

  if (!tenantId) {
    return (
      <>
        <PageHeader title="Workspace" />
        <EmptyState title="Select a workspace first">
          <p>Choose a workspace from the switcher to see and manage its settings.</p>
        </EmptyState>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Workspace">
        <p>
          What this workspace holds, who can reach it, and how to remove it.{" "}
          <Link to="/app/settings">Back to settings</Link>
        </p>
      </PageHeader>
      {notice ? (
        <p role="status" className="muted">
          {notice}
        </p>
      ) : null}
      {state.kind === "loading" ? <LoadingState label="Loading workspace…" /> : null}
      {state.kind === "unavailable" ? <ApiUnavailableState detail={state.detail} /> : null}
      {state.kind === "ready" ? (
        <>
          <div className="card-grid">
            <WorkspaceIdentityCard
              workspace={state.data.workspace}
              role={state.data.role}
              onRename={onRename}
              busy={renameBusy}
              error={renameError}
            />
            <MembersCard members={state.data.members} />
            <ServersCard servers={state.data.servers} />
            <PlanLimitsCard plan={state.data.plan} />
          </div>
          <div className="card-grid">
            <DangerZone
              role={state.data.role}
              preview={preview}
              typed={typed}
              onTypedChange={setTyped}
              busy={deleteBusy}
              error={deleteError}
              onOpen={() => void onOpenPreview()}
              onConfirm={() => void onConfirmDelete()}
              onCancel={onCancelDelete}
            />
          </div>
        </>
      ) : null}
    </>
  );
}
